import React, { useState, useEffect } from 'react';
import { ApiService } from '../services/api';
import MediaGalleryModal from './MediaGalleryModal';
import { EditableText } from './EditableText';
import { Camera, Trophy } from 'lucide-react';

interface AmbassadorSliceProps {
    editable?: boolean;
    bgConfig?: { bgUrl?: string; opacity?: number; bgColor?: string };
    isFirst?: boolean;
}

const DEFAULT_INTRO = 'Mit Chrigel Maurer haben wir den erfolgreichsten Gleitschirmpiloten der Welt als offiziellen Markenbotschafter und Berater für Streckenfliegen.app gewonnen. Seine jahrelange Erfahrung in extremem Terrain, seine strategische Brillanz und die kompromisslose Leidenschaft für unseren Sport machen ihn zum perfekten Partner.';
const DEFAULT_ACHIEVEMENTS = '8-facher Red Bull X-Alps Sieger\nMehrfacher Streckenflug-Weltmeister & Europameister\nRekordhalter im freien Streckenflug\nGewinner der X-Pyr und zahlloser XC-Wettbewerbe';

const AmbassadorSlice: React.FC<AmbassadorSliceProps> = ({ editable = false, bgConfig, isFirst = false }) => {
    const [ambassadorUrl, setAmbassadorUrl] = useState<string>('/chrigel-orange-jacket.png');
    const [ambassadorQuote, setAmbassadorQuote] = useState<string>('Das richtige Mindset und präzise Planung sind der halbe Flug. Streckenfliegen.app bringt dir beides.');
    const [ambassadorIntro, setAmbassadorIntro] = useState<string>(DEFAULT_INTRO);
    const [ambassadorAchievements, setAmbassadorAchievements] = useState<string>(DEFAULT_ACHIEVEMENTS);
    const [ambassadorName, setAmbassadorName] = useState<string>('Chrigel Maurer');
    const [ambassadorLabel, setAmbassadorLabel] = useState<string>('Markenbotschafter');
    const [ambassadorAchievementsTitle, setAmbassadorAchievementsTitle] = useState<string>('Ausgewählte Erfolge');

    const [mediaModalOpen, setMediaModalOpen] = useState(false);

    useEffect(() => {
        const loadAssets = async () => {
            try {
                const settings = await ApiService.getSystemSettings();
                if (settings?.['site_assets']) {
                    const parsed = JSON.parse(settings['site_assets']);
                    if (parsed.ambassadorUrl) setAmbassadorUrl(parsed.ambassadorUrl);
                    if (parsed.ambassadorQuote) setAmbassadorQuote(parsed.ambassadorQuote);
                    if (parsed.ambassadorIntro) setAmbassadorIntro(parsed.ambassadorIntro);
                    if (parsed.ambassadorAchievements) setAmbassadorAchievements(parsed.ambassadorAchievements);
                    if (parsed.ambassadorName) setAmbassadorName(parsed.ambassadorName);
                    if (parsed.ambassadorLabel) setAmbassadorLabel(parsed.ambassadorLabel);
                    if (parsed.ambassadorAchievementsTitle) setAmbassadorAchievementsTitle(parsed.ambassadorAchievementsTitle);
                }
            } catch (e) {
                console.error("Failed to load ambassador assets", e);
            }
        };
        loadAssets();

        const handleUpdate = (e: any) => {
            const assets = e.detail;
            if (assets?.ambassadorUrl) setAmbassadorUrl(assets.ambassadorUrl);
            if (assets?.ambassadorQuote) setAmbassadorQuote(assets.ambassadorQuote);
            if (assets?.ambassadorIntro) setAmbassadorIntro(assets.ambassadorIntro);
            if (assets?.ambassadorAchievements) setAmbassadorAchievements(assets.ambassadorAchievements);
            if (assets?.ambassadorName) setAmbassadorName(assets.ambassadorName);
            if (assets?.ambassadorLabel) setAmbassadorLabel(assets.ambassadorLabel);
            if (assets?.ambassadorAchievementsTitle) setAmbassadorAchievementsTitle(assets.ambassadorAchievementsTitle);
        };
        window.addEventListener('site-assets-update', handleUpdate);
        return () => window.removeEventListener('site-assets-update', handleUpdate);
    }, []);

    const handleChangeImage = async () => {
        if (!editable) return;
        setMediaModalOpen(true);
    };

    const handleMediaSelect = (url: string) => {
        if (url !== ambassadorUrl) {
            setAmbassadorUrl(url);
            saveToSettings({ ambassadorUrl: url });
        }
    };

    const saveToSettings = async (updates: any) => {
        try {
            const settings = await ApiService.getSystemSettings();
            const currentAssets = settings?.['site_assets'] ? JSON.parse(settings['site_assets']) : {};
            const newAssets = { ...currentAssets, ...updates };
            await ApiService.saveSystemSetting('site_assets', JSON.stringify(newAssets));
            window.dispatchEvent(new CustomEvent('site-assets-update', { detail: newAssets }));
        } catch (err) {
            console.error("Asset save fail:", err);
        }
    };

    return (
        <section id="ambassador" className={`${isFirst ? 'pt-[140px] lg:pt-[160px] pb-20' : 'py-20'} relative overflow-hidden border-t border-b border-white/5 ${bgConfig?.bgColor || bgConfig?.bgUrl ? 'bg-transparent' : 'bg-slate-900'}`}>
            <MediaGalleryModal 
                isOpen={mediaModalOpen} 
                onClose={() => setMediaModalOpen(false)} 
                onSelect={handleMediaSelect} 
            />
            {/* Background Effects */}
            <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-sky-500/10 rounded-full blur-[100px] -translate-y-1/2 translate-x-1/4 pointer-events-none"></div>
            <div className="absolute bottom-0 left-0 w-[600px] h-[600px] bg-indigo-500/10 rounded-full blur-[120px] translate-y-1/3 -translate-x-1/4 pointer-events-none"></div>

            <div className="container mx-auto px-6 relative z-10">
                <div className="max-w-6xl mx-auto">
                    
                    <div className="bg-slate-800/40 backdrop-blur-xl border border-white/10 rounded-[2rem] overflow-hidden shadow-2xl relative group">
                        <div className="flex flex-col lg:flex-row items-stretch">
                            
                            {/* Image Section */}
                            <div className="w-full lg:w-5/12 min-h-[400px] lg:min-h-full relative group/img overflow-hidden">
                                <img 
                                    src={ambassadorUrl} 
                                    alt="Chrigel Maurer - Markenbotschafter" 
                                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-1000 group-hover/img:scale-105"
                                    onError={(e: any) => {
                                        e.target.src = 'https://ui-avatars.com/api/?name=Chrigel+Maurer&background=0284c7&color=fff&size=512';
                                    }}
                                />

                                
                                {editable && (
                                    <button
                                        onClick={handleChangeImage}
                                        className="absolute top-4 left-4 bg-sky-600/90 hover:bg-sky-500 text-white px-3 py-1.5 rounded-lg text-xs font-bold shadow-lg backdrop-blur opacity-0 group-hover/img:opacity-100 transition-opacity z-20 flex items-center gap-2"
                                    >
                                        <Camera className="w-4 h-4 inline mr-1" /> Bild-URL ändern
                                    </button>
                                )}
                            </div>

                            {/* Text Section */}
                            <div className="w-full lg:w-7/12 p-8 lg:p-14 relative flex flex-col justify-center">
                                {/* Quote Icon Motif */}
                                <div className="absolute top-6 right-10 text-sky-500/10 text-8xl font-serif leading-none select-none pointer-events-none">"</div>
                                
                                <div className="relative z-10">
                                    <div className="inline-flex items-center gap-2 px-3 py-1 bg-sky-500/10 border border-sky-500/30 rounded-full text-[10px] font-bold text-sky-400 uppercase tracking-widest mb-6">
                                        <span className="w-2 h-2 rounded-full bg-sky-500 animate-pulse"></span>
                                        <EditableText 
                                            value={ambassadorLabel}
                                            onSave={(v) => { setAmbassadorLabel(v); saveToSettings({ ambassadorLabel: v }); }}
                                            mode={editable ? 'edit' : 'view'}
                                        />
                                    </div>

                                    <h2 className="text-3xl md:text-5xl font-black text-white uppercase tracking-tight mb-2 leading-tight">
                                        <EditableText 
                                            value={ambassadorName}
                                            onSave={(v) => { setAmbassadorName(v); saveToSettings({ ambassadorName: v }); }}
                                            mode={editable ? 'edit' : 'view'}
                                        />
                                    </h2>
                                    
                                    <div className="mb-6">
                                        <EditableText 
                                            value={ambassadorIntro}
                                            onSave={(v) => { setAmbassadorIntro(v); saveToSettings({ ambassadorIntro: v }); }}
                                            mode={editable ? 'edit' : 'view'}
                                            multiline
                                            className="text-sm md:text-base text-slate-300 leading-relaxed font-light"
                                        />
                                    </div>

                                    <div className="mb-8 p-6 bg-slate-900/50 rounded-2xl border border-sky-500/20">
                                        <h3 className="text-xs font-bold text-sky-400 uppercase tracking-widest mb-4 flex items-center gap-2">
                                            <span className="text-base"><Trophy className="w-4 h-4 inline mr-1" /></span> 
                                            <EditableText 
                                                value={ambassadorAchievementsTitle}
                                                onSave={(v) => { setAmbassadorAchievementsTitle(v); saveToSettings({ ambassadorAchievementsTitle: v }); }}
                                                mode={editable ? 'edit' : 'view'}
                                            />
                                        </h3>
                                        <EditableText 
                                            value={ambassadorAchievements}
                                            onSave={(v) => { setAmbassadorAchievements(v); saveToSettings({ ambassadorAchievements: v }); }}
                                            mode={editable ? 'edit' : 'view'}
                                            multiline
                                            renderView={(val) => (
                                                <ul className="space-y-3">
                                                    {val.split('\n').filter(Boolean).map((line, i) => (
                                                        <li key={i} className="flex items-start gap-3">
                                                            <svg className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                                                            </svg>
                                                            <span className="text-sm md:text-base text-slate-300 font-medium">{line}</span>
                                                        </li>
                                                    ))}
                                                </ul>
                                            )}
                                        />
                                    </div>

                                    <div className="mt-8 pt-8 border-t border-white/10">
                                        <EditableText 
                                            value={ambassadorQuote}
                                            onSave={(v) => { setAmbassadorQuote(v); saveToSettings({ ambassadorQuote: v }); }}
                                            mode={editable ? 'edit' : 'view'}
                                            multiline
                                            renderView={(val) => (
                                                <p className="text-lg md:text-xl md:leading-relaxed text-sky-100 font-light italic pl-4 border-l-2 border-sky-500">
                                                    "{val}"
                                                </p>
                                            )}
                                        />
                                    </div>

                                </div>
                            </div>
                            
                        </div>
                    </div>
                    
                </div>
            </div>
        </section>
    );
};

export default AmbassadorSlice;
